import { createSelector } from 'reselect';

import { authSelectors } from 'companion-app-components/flux/auth';
import { datasetsSelectors } from 'companion-app-components/flux/datasets';
import type RootState from 'companion-app-components/utils/redux-store/rootState';
import { 
  getAccountGroups, 
  isAccountGroupEnabled, 
  isEntityShareUser, 
  getCurrentAccountGroupName, 
} from 'companion-app-components/flux/account-groups/accountGroupsSelectors';
import { Dataset } from 'companion-app-components/flux/datasets/datasetsTypes';
import { deriveAccountGroupdIds, getSharedAccountGroups } from 'companion-app-components/flux/account-groups/accountGroupUtils';

const getDatasetId = (state: RootState) => authSelectors.getDatasetId(state);
const getDatasets = (state: RootState) => datasetsSelectors.getActiveDatasets(state);
const getWebFirstDataset = (state: RootState) => authSelectors.getIsWebfirstDataset(state);

export const getIsCurrentDatasetOwner = createSelector(
  getDatasetId, getDatasets, (datasetId, datasets) => {
    const currentDataset = datasets?.get(datasetId!);
    if (!currentDataset) return false;
    const ownDatasets = datasets?.filter((dataset) => !dataset.shared);
    return ownDatasets?.has(currentDataset.id!);
  },
);

export enum AllFeatures {
  ADD_ACCOUNTS = 'ADD_ACCOUNTS',
  ADD_BUDGET = 'ADD_BUDGET',
  FIX_ACCOUNT = 'FIX_ACCOUNT',
}

export const getIsFeatureAllowed = createSelector(
  getIsCurrentDatasetOwner, getWebFirstDataset, // eslint-disable-line function-call-argument-newline
  (_state, type: AllFeatures) => type, // eslint-disable-line function-call-argument-newline
  (isCurrentDatasetOwner, isWebFirstDataset, type) => {
    const allowedFeatures: AllFeatures[] = [];
    if (isCurrentDatasetOwner || !isWebFirstDataset) {
      allowedFeatures.push(...Object.values(AllFeatures));
    }
    return allowedFeatures.includes(type);
  },
);

export const hasDatasetWriteAccess = createSelector(
  [
    authSelectors.getCurrentDataset,
    datasetsSelectors.getDatasetsById,
    isAccountGroupEnabled,
    isEntityShareUser,
    getCurrentAccountGroupName,
    getAccountGroups,
  ],
  (
    currentDataset: Dataset,
    datasetsById,
    acGroupEnabled,
    entitySharedUser,
    selectedAcGroupName,
    accountGroups,
  ) => {
    const isShared = Boolean(currentDataset?.id && datasetsById.get(currentDataset.id)?.shared);
    if (!isShared || !acGroupEnabled || !entitySharedUser) {
      return true;
    }

    const sharedAccountGroups = getSharedAccountGroups(accountGroups, currentDataset);

    let selectedGroupsId;
    if (sharedAccountGroups.length > 1) {
      selectedGroupsId = sharedAccountGroups?.find((acOwner) => acOwner.name === selectedAcGroupName)?.id;
    } else if (sharedAccountGroups.length === 1) {
      selectedGroupsId = sharedAccountGroups[0].id;
    }

    const writeAcGroupIds = deriveAccountGroupdIds(
      currentDataset.itemAccessControl?.resources?.ACCOUNT_GROUP,
      ['write'],
    );

    return selectedGroupsId ? writeAcGroupIds.includes(selectedGroupsId) : false;
  },
);
